import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {HiChevronDown, HiSparkles} from 'react-icons/hi';
import {calculateWorkingDaysBetweenDates, defaultWorkingDays} from "../../../../utils/helperUtils";
import { ViewType } from '../../../../pages/Timeline/types';
import { StrategyEntityItemType } from '../../../../services/strategyMap';

interface BestFitPeriodSelectorProps {
  period: ViewType;
  data: StrategyEntityItemType;
  onChange: (period: ViewType, isBestMode?: boolean) => void;
  simFlag?: boolean;
  autoMode?: boolean;
}

const BestFitPeriodSelectorStrategyMap: React.FC<BestFitPeriodSelectorProps> = ({ 
  data, 
  period, 
  onChange,
  simFlag = false,
  autoMode = false 
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { orgWorkingDays = [], orgSchedule: organizationHolidays = [] } =
    useSelector((state: any) => state.organization);

  const workingDaysArray: number[] =
    Array.isArray(orgWorkingDays) && orgWorkingDays.length > 0
      ? orgWorkingDays
      : defaultWorkingDays;

  const collectDurations = (): number[] => {
    try {
      const entities = Array.isArray(data?.entities) ? data.entities : [];
      return entities
        .map((p: any) => {
          const start = simFlag ? p?.sim_data?.start_date : p?.start_date;
          const end = simFlag ? p?.sim_data?.end_date : p?.end_date;
          if (!start || !end) return null;
          return calculateWorkingDaysBetweenDates(
            String(start),
            String(end),
            workingDaysArray as number[],
            organizationHolidays as { date: string }[],
          );
        })
        .filter((d): d is number => typeof d === "number" && d > 0);
    } catch (e) {
      return [];
    }
  };

  const computeWeightedMeanByMagnitude = (values: number[]): number | null => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] || 1;
    let weightedSum = 0, weightTotal = 0;
    values.forEach(v => {
      const weight = Math.pow(v / median, 0.75);
      weightedSum += v * weight;
      weightTotal += weight;
    });
    return weightTotal === 0 ? null : Math.round(weightedSum / weightTotal);
  };

  const computeModeInfo = (values: number[]) => {
    const freqMap = new Map<number, number>();
    values.forEach(v => freqMap.set(v, (freqMap.get(v) || 0) + 1));
    let modeValue: number | null = null, maxFreq = 0;
    freqMap.forEach((freq, val) => {
      if (freq > maxFreq) { maxFreq = freq; modeValue = val; }
    });
    return { modeValue, maxFreq };
  };

  const getBucketFromDuration = (d: number): ViewType => {
    if (d <= 3) return "weekly";
    if (d <= 24) return "monthly";
    if (d <= 48) return "quarterly";
    return "yearly";
  };

  const bucketIndex = (b: ViewType): number => {
    const order: ViewType[] = ["weekly", "monthly", "quarterly", "yearly"];
    return order.indexOf(b);
  };

  const resolveBestFitPeriod = (): ViewType | null => {
    const durations = collectDurations();
    if (!durations.length) return null;
    const mean = computeWeightedMeanByMagnitude(durations);
    if (mean == null) return "monthly";
    let bucket = getBucketFromDuration(mean);
    const modeInfo = computeModeInfo(durations);
    if (durations.length <= 3 && (modeInfo.maxFreq / durations.length) >= 0.66 && modeInfo.modeValue) {
      const modeBucket = getBucketFromDuration(modeInfo.modeValue);
      if (bucketIndex(modeBucket) > bucketIndex(bucket)) bucket = modeBucket;
    }
    return bucket;
  };

  const bestFit = resolveBestFitPeriod() || "monthly";

  const handleBestFitSelect = () => {
    onChange(bestFit, true);
    setDropdownOpen(false);
  };

  // useEffect(() => {
  //   if (autoMode) {
  //     onChange(bestFit, true);
  //   }
  // }, [autoMode]);

  useEffect(() => {
    if (autoMode) {
      onChange(bestFit, true);
    }else{
      onChange(period, false);
    }
  }, [data]);

  return (
    <div className="relative ml-2">
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center px-3.5 py-1.5 text-sm rounded-lg bg-light-surfacePrimary dark:bg-slate-700 text-light-text-primary dark:text-gray-300 font-medium border border-light-border-default dark:border-gray-600 hover:bg-light-interactive-hover dark:hover:bg-slate-600 transition-colors duration-200"
      >
        <span className="mr-2 capitalize">{period} View</span>
        <HiChevronDown
          className={`text-light-text-secondary transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
        />
      </button>

      {dropdownOpen && (
        <>
          <div className="absolute top-full left-0 mt-1 w-44 bg-light-surfacePrimary dark:bg-secondary-dark rounded-lg shadow-lg border border-light-border-default dark:border-gray-700 overflow-hidden z-[9999] text-black">
            <button
              onClick={handleBestFitSelect}
              className={`flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm hover:bg-light-interactive-hover dark:hover:bg-slate-700 dark:text-gray-300 ${period === bestFit ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""}`}
            >
              Best Fit (Auto)
            </button>
            {["yearly", "quarterly", "monthly", "weekly"].map((v) => (
              <button
                key={v}
                onClick={() => {
                  onChange(v as ViewType, false);
                  setDropdownOpen(false);
                }}
                className={`block w-full text-left px-4 py-2.5 text-sm capitalize hover:bg-light-interactive-hover dark:hover:bg-slate-700 dark:text-gray-300 ${period === v ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""}`}
              >
                {v} View
              </button>
            ))}
          </div>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setDropdownOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default BestFitPeriodSelectorStrategyMap;